import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { api } from "../lib/api";
import { ArrowLeft, Send, Pause, Play, X, Users, Download } from "lucide-react";
import { ClickSplit } from "../components/click-split";
import { getToken } from "../lib/api";

interface Recipient {
  send_job_id: string; email: string; status: string;
  sent_at: string | null; delivered_at: string | null;
  brevo_message_id: string | null; error: string | null;
  last_event_type: string | null; last_event_at: string | null;
}

interface Campaign {
  id: string; name: string; status: string;
  totalRecipients: number; queuedCount: number; sentCount: number; deliveredCount: number;
  hardBounceCount: number; softBounceCount: number; openedCount: number; clickedCount: number;
  unsubscribedCount: number; complainedCount: number; suppressedCount: number;
  throttlePerMinute: number; pauseReason: string | null; pauseOnDailyCap: boolean;
  createdAt: string; startedAt: string | null; finishedAt: string | null;
}

export default function CampaignDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["campaign", id],
    queryFn: () => api<{ campaign: Campaign }>(`/api/campaigns/${id}`),
    refetchInterval: 5000,
  });

  const launch = useMutation({
    mutationFn: () => api(`/api/campaigns/${id}/launch`, { method: "POST" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["campaign", id] }),
  });
  const pause = useMutation({
    mutationFn: () => api(`/api/campaigns/${id}/pause`, { method: "POST" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["campaign", id] }),
  });
  const resume = useMutation({
    mutationFn: () => api(`/api/campaigns/${id}/resume`, { method: "POST" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["campaign", id] }),
  });
  const cancel = useMutation({
    mutationFn: () => api(`/api/campaigns/${id}/cancel`, { method: "POST" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["campaign", id] }),
  });

  if (isLoading || !data) return <div className="p-8">Loading…</div>;
  const c = data.campaign;
  const deliveryRate = c.sentCount > 0 ? ((c.deliveredCount / c.sentCount) * 100).toFixed(1) : "—";
  const openRate = c.deliveredCount > 0 ? ((c.openedCount / c.deliveredCount) * 100).toFixed(1) : "—";

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <Link to="/campaigns" className="text-sm text-indigo-600 hover:underline flex items-center gap-1 mb-3"><ArrowLeft size={14} /> Back</Link>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-gray-900">{c.name}</h1>
          <p className="text-sm text-gray-500 mt-1">Status: <span className="font-medium">{c.status}</span> · Throttle: {c.throttlePerMinute}/min · Created {new Date(c.createdAt).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {c.status === "draft" && (
            <button onClick={() => launch.mutate()} disabled={launch.isPending}
              className="bg-indigo-600 text-white rounded-lg px-3 py-1.5 text-sm flex items-center gap-1 hover:bg-indigo-700 disabled:opacity-50">
              <Send size={14} /> Launch
            </button>
          )}
          {c.status === "sending" && (
            <button onClick={() => pause.mutate()} className="bg-amber-600 text-white rounded-lg px-3 py-1.5 text-sm flex items-center gap-1 hover:bg-amber-700">
              <Pause size={14} /> Pause
            </button>
          )}
          {c.status === "paused" && (
            <button onClick={() => resume.mutate()} className="bg-indigo-600 text-white rounded-lg px-3 py-1.5 text-sm flex items-center gap-1 hover:bg-indigo-700">
              <Play size={14} /> Resume
            </button>
          )}
          {(c.status === "sending" || c.status === "paused" || c.status === "scheduled") && (
            <button onClick={() => { if (confirm("Cancel campaign?")) cancel.mutate(); }}
              className="bg-white border border-gray-300 text-gray-700 rounded-lg px-3 py-1.5 text-sm flex items-center gap-1 hover:bg-gray-50">
              <X size={14} /> Cancel
            </button>
          )}
          <ExportButton campaignId={c.id} />
        </div>
      </div>

      {launch.error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{(launch.error as Error).message}</div>}

      {c.status === "paused" && c.pauseReason === "daily_cap_reached" && (
        <div className="mb-4 text-sm bg-amber-50 border border-amber-200 rounded p-3 flex items-start gap-2">
          <Pause size={16} className="text-amber-700 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-amber-900">Auto-paused: workspace daily limit reached</p>
            <p className="text-amber-700 text-xs mt-1">Remaining recipients are queued and will resume automatically after midnight UTC.
              You can also click <strong>Resume</strong> manually if the cap has been raised in Settings.</p>
          </div>
        </div>
      )}
      {c.status === "paused" && c.pauseReason && c.pauseReason !== "daily_cap_reached" && (
        <div className="mb-4 text-sm bg-amber-50 border border-amber-200 rounded p-3">
          Paused: <span className="font-mono">{c.pauseReason}</span>
        </div>
      )}

      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
        <Stat label="Recipients" value={c.totalRecipients} />
        <Stat label="Queued" value={c.queuedCount} />
        <Stat label="Sent" value={c.sentCount} />
        <Stat label="Delivered" value={`${c.deliveredCount} (${deliveryRate}%)`} />
        <Stat label="Opens" value={`${c.openedCount} (${openRate}%)`} />
        <Stat label="Clicks (engaged - unsub)"
              valueNode={<ClickSplit clicked={c.clickedCount} unsubscribed={c.unsubscribedCount} />} />
      </div>

      <h2 className="text-base font-semibold text-gray-900 mb-3">Issues</h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <Stat label="Hard bounce" value={c.hardBounceCount} tone={c.hardBounceCount > 0 ? "warn" : "ok"} />
        <Stat label="Soft bounce" value={c.softBounceCount} />
        <Stat label="Complained" value={c.complainedCount} tone={c.complainedCount > 0 ? "warn" : "ok"} />
        <Stat label="Unsubscribed" value={c.unsubscribedCount} />
        <Stat label="Suppressed" value={c.suppressedCount} />
      </div>

      <RecipientsList campaignId={c.id} />
    </div>
  );
}

function RecipientsList({ campaignId }: { campaignId: string }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["campaign", campaignId, "recipients"],
    queryFn: () => api<{ recipients: Recipient[] }>(`/api/reports/campaign/${campaignId}/recipients`),
    enabled: open,
  });

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <button onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-3 flex items-center gap-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50 rounded-xl">
        <Users size={16} /> Recipients <span className="text-gray-400 font-normal">— {open ? "hide" : "show details per recipient"}</span>
      </button>
      {open && (
        <div className="border-t border-gray-100">
          {isLoading && <p className="text-sm text-gray-500 px-5 py-3">Loading…</p>}
          {data && (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-5 py-2">Email</th>
                  <th className="text-left px-5 py-2">Status</th>
                  <th className="text-left px-5 py-2">Latest event</th>
                  <th className="text-left px-5 py-2">Sent at</th>
                </tr>
              </thead>
              <tbody>
                {data.recipients.map((r) => (
                  <tr key={r.send_job_id} className="border-t border-gray-100">
                    <td className="px-5 py-2 font-mono text-xs">{r.email}</td>
                    <td className="px-5 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor(r.status)}`}>{r.status}</span>
                      {r.error && <div className="text-xs text-red-600 mt-1 truncate max-w-xs" title={r.error}>{r.error}</div>}
                    </td>
                    <td className="px-5 py-2">
                      {r.last_event_type ? (
                        <>
                          <span className="text-xs">{r.last_event_type}</span>
                          {r.last_event_at && <div className="text-xs text-gray-400">{new Date(r.last_event_at).toLocaleString()}</div>}
                        </>
                      ) : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-5 py-2 text-xs text-gray-500">{r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
                {data.recipients.length === 0 && (
                  <tr><td colSpan={4} className="px-5 py-6 text-center text-sm text-gray-400">No recipients (campaign not launched yet?)</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function ExportButton({ campaignId }: { campaignId: string }) {
  const [busy, setBusy] = useState(false);
  async function exportXlsx() {
    setBusy(true);
    try {
      const res = await fetch(`/api/campaigns/${campaignId}/export`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Export failed (HTTP ${res.status})`);
      const blob = await res.blob();
      // Pull the suggested filename from Content-Disposition; fall back
      // to a reasonable default if the header isn't available.
      const cd = res.headers.get("content-disposition") || "";
      const match = cd.match(/filename="([^"]+)"/);
      const fileName = match?.[1] ?? `campaign-${campaignId.slice(0, 8)}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <button onClick={exportXlsx} disabled={busy} title="Download an Excel file with campaign metadata + per-recipient status"
      className="bg-white border border-gray-300 text-gray-700 rounded-lg px-3 py-1.5 text-sm flex items-center gap-1 hover:bg-gray-50 disabled:opacity-50">
      <Download size={14} /> {busy ? "Preparing…" : "Export Excel"}
    </button>
  );
}

function statusColor(s: string): string {
  switch (s) {
    case "delivered": return "bg-emerald-100 text-emerald-700";
    case "sent": return "bg-blue-100 text-blue-700";
    case "queued": return "bg-gray-100 text-gray-700";
    case "bounced": return "bg-amber-100 text-amber-700";
    case "failed": return "bg-red-100 text-red-700";
    case "suppressed": return "bg-purple-100 text-purple-700";
    case "cancelled": return "bg-gray-100 text-gray-500";
    default: return "bg-gray-100 text-gray-700";
  }
}

function Stat({ label, value, valueNode, tone }: {
  label: string; value?: string | number; valueNode?: React.ReactNode; tone?: "ok" | "warn";
}) {
  return (
    <div className={`bg-white border rounded-xl p-4 ${tone === "warn" ? "border-amber-200" : "border-gray-200"}`}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-xl font-semibold mt-1 ${tone === "warn" ? "text-amber-700" : "text-gray-900"}`}>
        {valueNode ?? value}
      </div>
    </div>
  );
}
